<template>
  <component :is="isComponent ? 'div' : 'UPage'">
    <ConfigHeader
      v-if="!isComponent"
      :back-to="`/customers/${customerNo}/tenants`"
      :last-editor="config.lastEditor"
      :last-update="config.lastUpdate"
      :saving="saving"
      :tenant-name="tenantName"
      title="NFON Konfiguration"
      @save="saveConfig"
    />

    <component :is="isComponent ? 'div' : 'UPageBody'" :class="{ 'p-0': isComponent }">
      <div v-if="loading" class="flex justify-center py-6">
        <UIcon class="w-8 h-8 animate-spin text-muted" name="i-lucide-loader-circle" />
      </div>

      <div v-else class="space-y-6">
        <!-- Status & Activation -->
        <ConfigStatusCard
          :model-value="config.active"
          :disabled="!canWrite"
          title="NFON Telefonbuch-Synchronisation"
          description="Aktivieren oder deaktivieren Sie die automatische Synchronisation aller Rufnummern aus microtech in das zentrale NFON Cloudya-Telefonbuch."
          icon="i-lucide-phone"
          @update:model-value="onActiveToggle"
        />

        <USeparator class="my-8" />

        <ConfigChangelogAlert
          description="Stellen Sie sicher, dass die NFON Service Portal API-Zugangsdaten für Ihren K-Account freigeschaltet sind und der API-Benutzer über die erforderlichen Administrator- bzw. Telefonbuch-Rollen im NFON Portal verfügt."
        />

        <!-- Synchronisations-Status & Aktionen -->
        <UCard>
          <template #header>
            <div class="flex items-center justify-between flex-wrap gap-4">
              <div class="flex items-center gap-3">
                <UIcon name="i-lucide-refresh-cw" class="h-5 w-5 text-primary" />
                <div>
                  <h3 class="text-base font-semibold">Synchronisations-Status</h3>
                  <p class="text-xs text-muted">
                    Automatischer Sync: Inkrementell jede Minute (ModifyLSN) | Vollständiger Resync Sonntags 03:00 Uhr
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <UButton
                  :loading="refreshingStatus"
                  color="neutral"
                  variant="ghost"
                  icon="i-lucide-rotate-cw"
                  size="sm"
                  title="Status und Anzahl aktualisieren"
                  @click="refreshStatus"
                />
                <UButton
                  :loading="syncingIncremental"
                  :disabled="!canWrite || !config.active || syncingFull"
                  color="primary"
                  icon="i-lucide-refresh-cw"
                  label="Inkrementell syncen"
                  size="sm"
                  @click="() => triggerManualSync(false)"
                />
                <UButton
                  :loading="syncingFull"
                  :disabled="!canWrite || !config.active || syncingIncremental"
                  color="neutral"
                  variant="outline"
                  icon="i-lucide-rotate-ccw"
                  label="Vollständiger Resync"
                  size="sm"
                  @click="() => triggerManualSync(true)"
                />
              </div>
            </div>
          </template>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div class="text-xs text-muted">Status</div>
              <div class="text-lg font-semibold mt-1">
                <UBadge
                  :color="
                    config.lastSyncStatus === 'SUCCESS'
                      ? 'success'
                      : config.lastSyncStatus === 'PARTIAL'
                        ? 'warning'
                        : config.lastSyncStatus === 'FAILED'
                          ? 'error'
                          : 'neutral'
                  "
                  variant="subtle"
                >
                  {{ config.lastSyncStatus || 'Noch nie ausgeführt' }}
                </UBadge>
              </div>
            </div>

            <div class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div class="text-xs text-muted">Einträge in NFON</div>
              <div class="text-lg font-semibold font-mono mt-1">
                <span v-if="syncStatus?.nfonEntries !== undefined && syncStatus?.nfonEntries !== null">
                  {{ syncStatus.nfonEntries.toLocaleString('de-DE') }}
                </span>
                <span v-else-if="syncStatus?.totalEntries !== undefined && syncStatus?.totalEntries !== null">
                  {{ syncStatus.totalEntries.toLocaleString('de-DE') }}
                </span>
                <span v-else class="text-muted font-normal text-sm">-</span>
              </div>
            </div>

            <div class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div class="text-xs text-muted">Letzter Sync (Inkrementell)</div>
              <div class="text-sm font-medium mt-1">
                {{ formatDateTime(config.lastSync) || '-' }}
              </div>
            </div>

            <div class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div class="text-xs text-muted">Letzter Voll-Resync</div>
              <div class="text-sm font-medium mt-1">
                {{ formatDateTime(syncStatus?.lastFullSyncTime) || '-' }}
              </div>
            </div>

            <div class="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
              <div class="text-xs text-muted">Tracked LSNs (Anschriften / Kontakte)</div>
              <div class="text-sm font-mono mt-1">
                {{ syncStatus?.postalAddressesLastLsn ?? 0 }} / {{ syncStatus?.contactsLastLsn ?? 0 }}
              </div>
            </div>
          </div>

          <div v-if="config.lastSyncError" class="mt-4">
            <UAlert
              color="error"
              variant="subtle"
              icon="i-lucide-alert-triangle"
              title="Letzter Fehler"
              :description="config.lastSyncError"
            />
          </div>
        </UCard>

        <!-- NFON API Zugangsdaten -->
        <UCard>
          <template #header>
            <div class="flex items-center gap-3">
              <UIcon name="i-lucide-key" class="h-5 w-5 text-primary" />
              <h3 class="text-base font-semibold">NFON Service Portal API Zugangsdaten</h3>
            </div>
          </template>

          <div class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <UFormField
                label="API Key ID"
                required
                description="Der API Key ID aus dem NFON Service Portal (zugehöriger Benutzer benötigt Administrator- bzw. Telefonbuch-Rollen)."
              >
                <UInput
                  v-model="config.apiKeyId"
                  :disabled="!canWrite"
                  class="w-full font-mono text-sm"
                  placeholder="z. B. NFON_API_KEY_12345"
                  @update:model-value="onFieldChange"
                />
              </UFormField>

              <UFormField
                label="API Key Secret"
                required
                description="Der geheime API-Schlüssel für die HMAC-Signierung (wird verschlüsselt gespeichert)."
              >
                <div class="flex gap-2">
                  <UInput
                    v-model="config.apiKeySecret"
                    :disabled="!canWrite"
                    :type="showSecret ? 'text' : 'password'"
                    class="flex-1 font-mono text-sm"
                    placeholder="••••••••••••••••"
                    @update:model-value="onFieldChange"
                  />
                  <UButton
                    :icon="showSecret ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    color="neutral"
                    variant="ghost"
                    @click="showSecret = !showSecret"
                  />
                </div>
              </UFormField>

              <UFormField
                label="NFON Kundennummer (K-Account)"
                required
                description="Ihre NFON Kundennummer, z. B. K12345 (muss dem Zugriffsbereich des API-Benutzers entsprechen)."
              >
                <UInput
                  v-model="config.customerId"
                  :disabled="!canWrite"
                  class="w-full font-mono text-sm"
                  placeholder="K12345"
                  @update:model-value="onFieldChange"
                />
              </UFormField>

              <UFormField
                label="Standard-Ländervorwahl"
                description="Internationale Vorwahl für lokale Rufnummern ohne Länderzuordnung."
              >
                <UInput
                  v-model="config.defaultCountryCode"
                  :disabled="!canWrite"
                  class="w-full"
                  placeholder="+49"
                  @update:model-value="onFieldChange"
                />
              </UFormField>
            </div>

            <!-- Verbindungstest Bereich -->
            <div class="pt-4 border-t border-gray-200 dark:border-gray-800 space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="text-sm font-semibold">Verbindungstest</h4>
                  <p class="text-xs text-muted">
                    Prüft die Gültigkeit der HMAC-Signatur und liest das zentrale Telefonbuch aus.
                  </p>
                </div>
                <UButton
                  :loading="testing"
                  :disabled="!canWrite || !config.apiKeyId || !config.customerId"
                  color="neutral"
                  icon="i-lucide-plug"
                  label="Verbindung testen"
                  variant="outline"
                  @click="testConnection"
                />
              </div>

              <div v-if="testResult">
                <UAlert
                  :color="testResult.success ? 'success' : 'error'"
                  :icon="testResult.success ? 'i-lucide-check-circle' : 'i-lucide-alert-circle'"
                  :title="testResult.success ? 'Verbindung erfolgreich' : 'Verbindungsfehler'"
                  :description="testResult.message"
                  variant="subtle"
                />
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </component>
  </component>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, reactive, ref } from 'vue'
import axios from 'axios'
import type { NfonConfigDto, NfonTestResult, NfonSyncStatus } from '@/types'
import { backendErrorMessage } from '@/utils/api-errors'
import ConfigHeader from '@/components/ConfigHeader.vue'
import ConfigStatusCard from '@/components/ConfigStatusCard.vue'
import ConfigChangelogAlert from '@/components/ConfigChangelogAlert.vue'

withDefaults(
  defineProps<{
    customerNo?: string
    tenantName?: string
    isComponent?: boolean
    canWrite?: boolean
  }>(),
  {
    customerNo: '',
    tenantName: '',
    isComponent: false,
    canWrite: true,
  },
)

const emit = defineEmits<{
  (e: 'dirty'): void
}>()

const toast = useToast()
const loading = ref(true)
const saving = ref(false)
const testing = ref(false)
const refreshingStatus = ref(false)
const syncingIncremental = ref(false)
const syncingFull = ref(false)
const showSecret = ref(false)
const testResult = ref<NfonTestResult | null>(null)
const syncStatus = ref<NfonSyncStatus | null>(null)

const config = reactive<NfonConfigDto>({
  active: false,
  apiKeyId: '',
  apiKeySecret: '',
  customerId: '',
  baseUrl: 'https://portal-api.nfon.net:8090',
  defaultCountryCode: '+49',
  syncIntervalCron: '* * * * *',
})

const onFieldChange = () => {
  emit('dirty')
}

const onActiveToggle = (val: boolean) => {
  config.active = val
  emit('dirty')
}

const formatDateTime = (val?: string) => {
  if (!val) return ''
  try {
    const d = new Date(val)
    if (isNaN(d.getTime())) return val
    return d.toLocaleString('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return val
  }
}

const loadConfig = async () => {
  loading.value = true
  try {
    const [cfgRes, statusRes] = await Promise.allSettled([
      axios.get<NfonConfigDto>('/api/nfon/v1/config'),
      axios.get<NfonSyncStatus>('/api/nfon/v1/sync/status'),
    ])

    if (cfgRes.status === 'fulfilled' && cfgRes.value.data) {
      Object.assign(config, cfgRes.value.data)
      if (!config.baseUrl) config.baseUrl = 'https://portal-api.nfon.net:8090'
      if (!config.defaultCountryCode) config.defaultCountryCode = '+49'
      if (!config.syncIntervalCron) config.syncIntervalCron = '* * * * *'
    }

    if (statusRes.status === 'fulfilled' && statusRes.value.data) {
      syncStatus.value = statusRes.value.data
    }
  } catch (error) {
    console.error('Failed to load NFON configuration', error)
  } finally {
    loading.value = false
  }
}

const saveConfig = async () => {
  saving.value = true
  try {
    const res = await axios.put<NfonConfigDto>('/api/nfon/v1/config', config)
    if (res.data) {
      Object.assign(config, res.data)
    }
    toast.add({
      title: 'Erfolg',
      description: 'NFON-Konfiguration erfolgreich gespeichert.',
      color: 'success',
    })
  } catch (error) {
    console.error('Failed to save NFON configuration', error)
    toast.add({
      title: 'Fehler',
      description: backendErrorMessage(error, 'Fehler beim Speichern der NFON-Konfiguration.'),
      color: 'error',
    })
    throw error
  } finally {
    saving.value = false
  }
}

const refreshStatus = async () => {
  refreshingStatus.value = true
  try {
    const statusRes = await axios.get<NfonSyncStatus>('/api/nfon/v1/sync/status')
    if (statusRes.data) {
      syncStatus.value = statusRes.data
      const count = statusRes.data.nfonEntries ?? statusRes.data.totalEntries ?? 0
      toast.add({
        title: 'Status aktualisiert',
        description: `Aktuelle Einträge in NFON: ${count.toLocaleString('de-DE')}`,
        color: 'success',
      })
    }
  } catch (error) {
    console.error('Failed to refresh sync status', error)
    toast.add({
      title: 'Fehler',
      description: backendErrorMessage(error, 'Status konnte nicht aktualisiert werden.'),
      color: 'error',
    })
  } finally {
    refreshingStatus.value = false
  }
}

const testConnection = async () => {
  testing.value = true
  testResult.value = null
  try {
    const res = await axios.post<NfonTestResult>('/api/nfon/v1/config/test', config)
    testResult.value = res.data
    if (res.data.success) {
      if (res.data.phoneBookEntries !== undefined) {
        if (syncStatus.value) {
          syncStatus.value.nfonEntries = res.data.phoneBookEntries
          syncStatus.value.totalEntries = res.data.phoneBookEntries
        }
      }
      toast.add({
        title: 'Verbindung erfolgreich',
        description: res.data.message,
        color: 'success',
      })
    } else {
      toast.add({
        title: 'Verbindungsfehler',
        description: res.data.message,
        color: 'error',
      })
    }
  } catch (error) {
    testResult.value = {
      success: false,
      nfonConnected: false,
      message: backendErrorMessage(error, 'Verbindung zu NFON fehlgeschlagen.'),
    }
  } finally {
    testing.value = false
  }
}

// A full resync runs in the backend after the request returns (202): purge, settle delay and
// thousands of creates take longer than the gateway keeps a request open. Poll the status until done.
const SYNC_POLL_INTERVAL_MS = 5000
let unmounted = false

const waitForBackgroundSync = async () => {
  toast.add({
    title: 'Vollständiger Resync gestartet',
    description: 'Der Resync läuft im Hintergrund und kann mehrere Minuten dauern.',
    color: 'info',
  })
  while (!unmounted) {
    await new Promise(resolve => setTimeout(resolve, SYNC_POLL_INTERVAL_MS))
    if (unmounted) return
    let status: NfonSyncStatus
    try {
      const statusRes = await axios.get<NfonSyncStatus>('/api/nfon/v1/sync/status')
      status = statusRes.data
    } catch {
      continue
    }
    syncStatus.value = status
    if (status.isSyncing) continue

    const last = status.lastSync
    if (last) {
      config.lastSync = last.endTime
      config.lastSyncStatus = last.status
      config.lastSyncCount = last.totalFound
      config.lastSyncError = last.errors?.join('; ') || ''
    }
    const ok = last?.status === 'SUCCESS'
    toast.add({
      title: ok ? 'Vollständiger Resync abgeschlossen' : 'Vollständiger Resync mit Fehlern beendet',
      description: ok
        ? `${last?.totalFound ?? 0} Einträge synchronisiert.`
        : last?.errors?.join('; ') || `Status: ${last?.status ?? 'unbekannt'}`,
      color: ok ? 'success' : 'error',
    })
    return
  }
}

onUnmounted(() => {
  unmounted = true
})

const triggerManualSync = async (full = false) => {
  if (full) {
    syncingFull.value = true
  } else {
    syncingIncremental.value = true
  }

  try {
    const url = full ? '/api/nfon/v1/sync?full=true' : '/api/nfon/v1/sync'
    const res = await axios.post(url)
    if (res.status === 202) {
      await waitForBackgroundSync()
      return
    }
    toast.add({
      title: full ? 'Vollständiger Resync abgeschlossen' : 'Inkrementeller Sync abgeschlossen',
      description: res.data?.message || 'Telefonbuch erfolgreich synchronisiert.',
      color: 'success',
    })
    if (res.data?.syncLog) {
      config.lastSync = res.data.syncLog.endTime
      config.lastSyncStatus = res.data.syncLog.status
      config.lastSyncCount = res.data.syncLog.totalFound
      config.lastSyncError = res.data.syncLog.errors?.join('; ') || ''
    }
    // Refresh status metrics
    try {
      const statusRes = await axios.get<NfonSyncStatus>('/api/nfon/v1/sync/status')
      if (statusRes.data) {
        syncStatus.value = statusRes.data
      }
    } catch {
      // ignore status refresh error
    }
  } catch (error) {
    console.error('Manual sync failed', error)
    toast.add({
      title: 'Synchronisationsfehler',
      description: backendErrorMessage(error, 'Manueller Sync fehlgeschlagen.'),
      color: 'error',
    })
  } finally {
    if (full) {
      syncingFull.value = false
    } else {
      syncingIncremental.value = false
    }
  }
}

onMounted(() => {
  loadConfig()
})

defineExpose({
  saveConfig,
  triggerManualSync,
  testConnection,
  refreshStatus,
  config,
  syncStatus,
  formatDateTime,
  onActiveToggle,
})
</script>
